import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import './AdminPurchase.css';

function AdminPurchase() {
  const { user } = useContext(AuthContext);
  const [memberId, setMemberId] = useState('');
  const [member, setMember] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [recentTransactions, setRecentTransactions] = useState([]);

  // Lookup member by ID (scan or manual entry)
  const lookupMember = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setMember(null);

    try {
      const response = await api.post('/pos/lookup', { membershipId: memberId.toUpperCase() });
      setMember(response.data.member);
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.error || 'Member not found'}`);
    } finally {
      setLoading(false);
    }
  };

  // Record purchase and add points
  const recordPurchase = async (e) => {
    e.preventDefault();
    if (!member || !amount) return;

    setLoading(true);
    setMessage('');

    try {
      const purchaseAmount = parseFloat(amount);
      
      // Calculate points with tier multiplier
      const basePoints = Math.floor(purchaseAmount); // 1 point per $1
      const multiplier = getTierMultiplier(member.tier);
      const pointsEarned = Math.floor(basePoints * multiplier);

      // Record the purchase
      await api.post('/admin/record-purchase', {
        userId: member.id,
        membershipId: member.membershipId,
        amount: purchaseAmount,
        pointsEarned: pointsEarned,
        tier: member.tier,
        multiplier: multiplier,
        recordedBy: user.name || user.email,
        timestamp: new Date().toISOString()
      });

      setMessage(`✅ Purchase recorded! ${member.name} earned ${pointsEarned} points (Tier: ${member.tier}, ${multiplier}x multiplier)`);
      
      // Add to recent transactions
      setRecentTransactions(prev => [{
        id: Date.now(),
        memberName: member.name,
        membershipId: member.membershipId,
        amount: purchaseAmount,
        pointsEarned: pointsEarned,
        tier: member.tier,
        recordedBy: user.name || user.email,
        timestamp: new Date().toLocaleString()
      }, ...prev].slice(0, 10));

      // Reset form
      setAmount('');
      setMember(null);
      setMemberId('');

    } catch (error) {
      setMessage(`❌ ${error.response?.data?.error || 'Failed to record purchase'}`);
    } finally {
      setLoading(false);
    }
  };

  const getTierMultiplier = (tier) => {
    const multipliers = { bronze: 1, silver: 1.5, gold: 2, platinum: 3 };
    return multipliers[tier] || 1;
  };

  const calculatePoints = () => {
    if (!amount || !member) return 0;
    const basePoints = Math.floor(parseFloat(amount) || 0);
    const multiplier = getTierMultiplier(member.tier);
    return Math.floor(basePoints * multiplier);
  };

  return (
    <div className="container admin-purchase-page">
      <div className="page-header">
        <h1>🖥️ Staff: Record Purchase</h1>
        <p>Lookup member by card scan or ID, then record their purchase</p>
      </div>

      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Step 1: Lookup Member */}
      <div className="card lookup-card">
        <h3>Step 1: Lookup Member</h3>
        <form onSubmit={lookupMember}>
          <div className="input-group">
            <label>Scan Membership Card or Enter ID:</label>
            <input
              type="text"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              placeholder="e.g., A1B2C3D4"
              maxLength={8}
              className="member-id-input"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading || memberId.length < 8}>
            {loading ? 'Looking up...' : '🔍 Lookup Member'}
          </button>
        </form>
      </div>

      {/* Step 2: Member Found - Show Details */}
      {member && (
        <div className="card member-details-card">
          <h3>✅ Member Found</h3>
          <div className="member-info-display">
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">{member.name}</span>
            </div>
            <div className="info-row">
              <span className="label">Email:</span>
              <span className="value">{member.email}</span>
            </div>
            <div className="info-row">
              <span className="label">Member ID:</span>
              <span className="value code">{member.membershipId}</span>
            </div>
            <div className="info-row">
              <span className="label">Current Tier:</span>
              <span className={`value tier-badge ${member.tier}`}>{member.tier.toUpperCase()}</span>
            </div>
            <div className="info-row">
              <span className="label">Current Points:</span>
              <span className="value points">{member.points.toLocaleString()}</span>
            </div>
            <div className="info-row">
              <span className="label">Lifetime Points:</span>
              <span className="value">{member.lifetimePoints?.toLocaleString() || 0}</span>
            </div>
          </div>

          <hr />

          {/* Step 3: Enter Purchase */}
          <h3>Step 2: Record Purchase</h3>
          <form onSubmit={recordPurchase}>
            <div className="input-group">
              <label>Purchase Amount ($):</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                min="0.01"
                step="0.01"
                className="amount-input"
              />
            </div>

            {/* Points Preview */}
            {amount > 0 && (
              <div className="points-preview">
                <p>Purchase: <strong>${parseFloat(amount).toFixed(2)}</strong></p>
                <p>Tier Multiplier: <strong>{getTierMultiplier(member.tier)}x</strong></p>
                <p>Points to Add: <strong className="points-earned">{calculatePoints()}</strong></p>
              </div>
            )}

            <button 
              type="submit" 
              className="btn btn-success btn-large" 
              disabled={loading || !amount || amount <= 0}
            >
              {loading ? 'Recording...' : '💳 Record Purchase & Add Points'}
            </button>
          </form>
        </div>
      )}

      {/* Recent Transactions Audit Trail */}
      {recentTransactions.length > 0 && (
        <div className="card audit-trail-card">
          <h3>📝 Recent Transactions (This Session)</h3>
          <table className="audit-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Member</th>
                <th>ID</th>
                <th>Amount</th>
                <th>Points Added</th>
                <th>Tier</th>
                <th>Recorded By</th>
              </tr>
            </thead>
            <tbody>
              {recentTransactions.map((tx) => (
                <tr key={tx.id}>
                  <td>{tx.timestamp}</td>
                  <td>{tx.memberName}</td>
                  <td className="code">{tx.membershipId}</td>
                  <td>${tx.amount.toFixed(2)}</td>
                  <td className="points">+{tx.pointsEarned}</td>
                  <td>{tx.tier}</td>
                  <td>{tx.recordedBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Instructions */}
      <div className="card instructions-card">
        <h3>📋 How to Use:</h3>
        <ol>
          <li><strong>Scan the customer's phone</strong> - they open /membership and show QR code</li>
          <li><strong>Or ask for their Member ID</strong> - 8 characters from their card</li>
          <li><strong>Enter purchase amount</strong> - system auto-calculates points with tier bonus</li>
          <li><strong>Click "Record Purchase"</strong> - points added instantly to their account</li>
          <li><strong>Audit trail saved</strong> - who recorded it, when, how much</li>
        </ol>
        <p className="note">
          <strong>Note:</strong> All transactions are logged for audit purposes. 
          Full transaction history available in Admin Dashboard.
        </p>
      </div>
    </div>
  );
}

export default AdminPurchase;
